import type { SanityPageBuilderGallerySection, SanityPageBuilderGallerySlide, SanityPageBuilderSection } from "@ouster/shared";
import { GalleryBlockView } from "~/features/blocks/inventory/gallery";
import { GallerySlideView, type GallerySlideContentPlacement } from "~/features/blocks/inventory/gallery/gallery-slide";
import { GalleryControlView } from "~/features/blocks/inventory/gallery/gallery-controls";
import { toCraftLinkArray } from "~/features/sanity/link";
import { SanityRichText } from "~/features/sanity/rich-text";
import { hasRenderableSanityMedia, SanityMediaFigure } from "./media-figure";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderGallerySection;
  };
};
type GalleryViewSlide = {
  _key?: SanityPageBuilderGallerySlide["_key"];
  contentPlacement?: GallerySlideContentPlacement;
  label?: SanityPageBuilderGallerySlide["label"];
  link?: ReturnType<typeof toCraftLinkArray>;
  media?: SanityPageBuilderGallerySlide["media"];
  supportLink?: ReturnType<typeof toCraftLinkArray>;
  text?: React.ReactNode;
  title?: SanityPageBuilderGallerySlide["title"];
};
export function SanityGallerySection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const layout = sectionContent.layout || "boxed";
  const slides = sectionContent.slides?.map<GalleryViewSlide>(slide => ({
    _key: slide._key,
    contentPlacement: (slide.contentPlacement || "inset") as GallerySlideContentPlacement,
    label: slide.label,
    link: toCraftLinkArray(slide.link),
    media: slide.media,
    supportLink: toCraftLinkArray(slide.supportLink),
    text: slide.text?.length ? <SanityRichText value={slide.text} /> : undefined,
    title: slide.title
  })) ?? [];
  const text = sectionContent.text?.length ? <SanityRichText value={sectionContent.text} /> : undefined;
  return <GalleryBlockView<GalleryViewSlide> anchor={{
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    name: sectionSettings?.anchorName
  }} eyebrow={sectionContent.eyebrow} getSlideKey={(slide, index) => slide._key || index} hasInsetDividers={sectionContent.hasInsetDividers ?? true} hasTopDivider={sectionSettings?.hasTopDivider ?? true} headline={sectionContent.headline} isHighlighted={sectionContent.isHighlighted ?? false} layout={layout} mode={sectionSettings?.mode || "light"} renderControl={(slide, _index, state) => <GalleryControlView {...state} text={slide.text} title={slide.title} />} renderSlide={slide => <GallerySlideView contentPlacement={slide.contentPlacement} label={slide.label} link={slide.link} renderDesktopMedia={() => hasRenderableSanityMedia(slide.media) ? <SanityMediaFigure media={slide.media} fit={slide.contentPlacement === "inset" ? "cover" : "fill"} ratio={slide.contentPlacement === "inset" ? undefined : "2/1"} /> : null} renderMobileMedia={() => hasRenderableSanityMedia(slide.media) ? <SanityMediaFigure media={slide.media} ratio={slide.contentPlacement === "inset" ? "5/6" : "3/2"} /> : null} supportLink={slide.supportLink} text={slide.text} title={slide.title} />} sectionProps={{
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type
  }} slides={slides} text={text} theme={sectionSettings?.theme || "white"} />;
}